import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
  PermitFieldType,
} from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FieldValuesSelector from '../../../../FieldConfigurator/components/DynamicFilters/components/DynamicFiltersList/components/FieldValuesSelector'
import FormFieldsModal from '../../../../FormFieldsModal'
import { IConditionalSelectOption } from '../types/IConditionalSelectOption'
import ConditionalRuleHeader from './ConditionalRuleHeader'

interface IProps {
  field: IPermitTypeField
  usesPickerFlow: boolean
  availableSelectOptions: IConditionalSelectOption[]
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]
  restrictedFieldTypes: PermitFieldType[]

  onCancel: () => void
  onNewRuleValueSelect: (event: React.ChangeEvent<HTMLSelectElement>) => void
  onLocationApply: (selectedIds: string[], fieldType?: PermitFieldType) => void
}

const SCROLL_DELAY = 100

function NewRuleForm({
  field,
  usesPickerFlow,
  availableSelectOptions,
  restrictedLocationIds,
  restrictedLocationTypes,
  restrictedFieldTypes,
  onCancel,
  onNewRuleValueSelect,
  onLocationApply,
}: IProps) {
  const ruleRef = useRef<HTMLDivElement>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  useEffect(() => {
    const ref = ruleRef.current
    if (ref) {
      setTimeout(() => {
        ref?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }, SCROLL_DELAY)
    }
  }, [])

  const filter = useMemo(
    () =>
      ({
        fieldId: field.id,
        fieldValues: selectedIds,
      }) as ITypeFieldFilterOptions,
    [field.id, selectedIds],
  )

  const handleConfirm = () => {
    if (selectedIds.length > 0) {
      onLocationApply(selectedIds)
    }
  }

  if (usesPickerFlow) {
    const hasSelections = selectedIds.length > 0

    return (
      <div
        ref={ruleRef}
        className="mb12 ba-palette-brand-lighter brada8 mw-fit-content"
      >
        <ConditionalRuleHeader
          field={field}
          isCreation
          isExpanded={hasSelections}
          availableSelectOptions={[]}
          renderValueSelector={
            <FieldValuesSelector
              filter={filter}
              selectedField={field}
              isEditMode
              restrictedLocationIds={restrictedLocationIds}
              restrictedLocationTypes={restrictedLocationTypes}
              onChange={setSelectedIds}
            />
          }
          isPickerEditMode={true}
          isEditDisabled={!hasSelections}
          onEditToggle={handleConfirm}
          onRemoveRule={onCancel}
          onNewRuleValueSelect={onNewRuleValueSelect}
        />
        {hasSelections && (
          <FormFieldsModal
            addNewField={(fieldType: PermitFieldType) => {
              onLocationApply(selectedIds, fieldType)
            }}
            restrictedFields={restrictedFieldTypes}
          >
            <Button variant="primary-ghost" size="lg">
              <PlusIcon data-icon="inline-start" />
              {Localization.translator.addDependentField}
            </Button>
          </FormFieldsModal>
        )}
      </div>
    )
  }

  return (
    <div
      ref={ruleRef}
      className="mb12 ba-palette-brand-lighter brada8 mw-fit-content"
    >
      <ConditionalRuleHeader
        field={field}
        isCreation
        availableSelectOptions={availableSelectOptions}
        onRemoveRule={onCancel}
        onNewRuleValueSelect={onNewRuleValueSelect}
      />
    </div>
  )
}

export default observer(NewRuleForm)
